import { useEffect } from 'react';
import { useTeamsData } from '@lib/hooks/useTeamsData';
import { useRealtimeSyncStore } from './store';

/**
 * useRealtimeSync — keeps this web-app instance attached to the RTDB control plane so
 * the extension and embeds can see that the app is online for this team owner.
 *
 * Mount once, high in the tree (TeamsGate). Presence is published by the leader-elected
 * frame (see leaderTransport) rather than directly here, so multiple embed iframes don't
 * each open a socket. We just hold a transport ref for the mount's lifetime and register
 * the current user id; the leader publishes presence for it (and re-publishes on change).
 */
export const useRealtimeSync = (): void => {
  const { userId, ownerKey } = useTeamsData((s) => ({
    // Prefer the signed-in member's id; fall back to the owner id from the team token.
    userId: (s.user?.id as string | undefined) ?? s.teamContext?.oid ?? undefined,
    // Same owner key useRealtimeSyncState uses — a change means a team switch.
    ownerKey: s.currentTeamBillingAccountOwnerID ?? s.teamContext?.oid ?? null
  }));

  const connect = useRealtimeSyncStore((s) => s.connect);
  const setPresenceIdentity = useRealtimeSyncStore((s) => s.setPresenceIdentity);
  const setOwnerKey = useRealtimeSyncStore((s) => s.setOwnerKey);

  // Keep the store's owner key current: this hook holds a permanent transport
  // ref, so on a team switch it is the store (not consumer re-mounts) that must
  // restart the transport and re-auth under the new owner.
  useEffect(() => {
    if (ownerKey) setOwnerKey(String(ownerKey));
  }, [ownerKey, setOwnerKey]);

  // Hold the shared transport open for as long as the app is mounted.
  useEffect(() => connect(), [connect]);

  // Register (and update) whose presence the leader should publish.
  useEffect(() => {
    setPresenceIdentity(userId ?? null);
  }, [userId, setPresenceIdentity]);
};

export default useRealtimeSync;
